import React, { useState } from "react"
import { Box, Button, Divider, Input, Select, Text } from "@chakra-ui/react"
import { createDoctor } from "../../Http/Index"
import { ShowAlert } from "../../GlobalComponent/Alerts/Alert"
import { Label, SectionHeader, MultiSelectDropdown } from "./shared.types"

interface DoctorForm {
  name: string; email: string; phone: string; profile_photo: File | null
  qualification: string; specializations: string[]; years_of_experience: string
  registration_number: string; registration_council: string; registration_expiry: string
  doctor_type: string; organization_name: string
  address_line1: string; address_line2: string; city: string; state: string; country: string; pincode: string
  languages: string[]; time_slot_start: string; time_slot_end: string; time_slot_days: string[]
  consultation_modes: string[]; status: string; is_verified: boolean
}

const INITIAL: DoctorForm = {
  name: "", email: "", phone: "", profile_photo: null,
  qualification: "", specializations: [], years_of_experience: "",
  registration_number: "", registration_council: "", registration_expiry: "",
  doctor_type: "", organization_name: "",
  address_line1: "", address_line2: "", city: "", state: "", country: "", pincode: "",
  languages: [], time_slot_start: "", time_slot_end: "", time_slot_days: [],
  consultation_modes: [], status: "inactive", is_verified: false,
}

const LANGUAGES = [
  "English","Hindi","Bengali","Telugu","Marathi","Tamil","Gujarati","Urdu",
  "Kannada","Odia","Malayalam","Punjabi","Assamese","Maithili","Sanskrit",
  "Kashmiri","Konkani","Sindhi","Nepali","Manipuri (Meitei)","Dogri","Bodo",
  "Santhali","Tulu","Rajasthani","Bhojpuri",
]

const SPECIALIZATIONS = [
  "General Medicine","General Surgery","Cardiology","Neurology","Neurosurgery",
  "Nephrology","Gastroenterology","Pulmonology","Endocrinology","Oncology",
  "Orthopedics","Pediatrics","Gynecology","Obstetrics","Dermatology",
  "Psychiatry","Radiology","Anesthesiology","Ophthalmology","ENT (Otorhinolaryngology)",
  "Urology","Plastic Surgery","Vascular Surgery","Cardiothoracic Surgery","Rheumatology",
  "Emergency Medicine","Family Medicine","Pathology","Microbiology","Internal Medicine",
  "Diabetology","Hematology","Immunology","Allergy & Immunology","Infectious Disease",
  "Nuclear Medicine","Pain Medicine","Sports Medicine","Geriatrics","Dental Surgery",
  "Oral & Maxillofacial Surgery","Prosthodontics","Periodontics","Orthodontics",
  "Physiotherapy","Ayurveda","Homeopathy",
]

const COUNCILS = [
  "National Medical Commission (NMC)","Andhra Pradesh Medical Council","Assam Medical Council",
  "Bihar Medical Council","Chhattisgarh Medical Council","Delhi Medical Council",
  "Goa Medical Council","Gujarat Medical Council","Haryana Medical Council",
  "Himachal Pradesh Medical Council","Jammu & Kashmir Medical Council","Jharkhand Medical Council",
  "Karnataka Medical Council","Kerala Medical Council","Madhya Pradesh Medical Council",
  "Maharashtra Medical Council","Manipur Medical Council","Meghalaya Medical Council",
  "Mizoram Medical Council","Nagaland Medical Council","Odisha Medical Council",
  "Punjab Medical Council","Rajasthan Medical Council","Sikkim Medical Council",
  "Tamil Nadu Medical Council","Telangana Medical Council","Tripura Medical Council",
  "Uttar Pradesh Medical Council","Uttarakhand Medical Council","West Bengal Medical Council",
]

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0")
  const m = i % 2 === 0 ? "00" : "30"
  const val = `${h}:${m}`
  const hour = Math.floor(i / 2)
  const label = `${hour === 0 ? 12 : hour > 12 ? hour - 12 : hour}:${m} ${hour < 12 ? "AM" : "PM"}`
  return { val, label }
})

interface Props { onCreated: () => void }

export const CreateProfile: React.FC<Props> = ({ onCreated }) => {
  const [form, setForm] = useState<DoctorForm>(INITIAL)
  const [loading, setLoading] = useState(false)

  const set = (field: keyof DoctorForm, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }))

  const handleSubmit = async () => {
    if (!form.name) return ShowAlert("warning", "Full Name is required")
    if (!form.email) return ShowAlert("warning", "Email is required")
    if (!form.phone) return ShowAlert("warning", "Phone is required")
    if (!form.qualification) return ShowAlert("warning", "Qualification is required")
    if (form.specializations.length === 0) return ShowAlert("warning", "Specialization is required")
    if (!form.years_of_experience) return ShowAlert("warning", "Years of Experience is required")
    if (!form.doctor_type) return ShowAlert("warning", "Doctor Type is required")
    if (!form.registration_number) return ShowAlert("warning", "Registration Number is required")
    if (!form.registration_council) return ShowAlert("warning", "Registration Council is required")
    if (!form.registration_expiry) return ShowAlert("warning", "Registration Expiry is required")
    if (form.languages.length === 0) return ShowAlert("warning", "At least one Language is required")
    if (form.consultation_modes.length === 0) return ShowAlert("warning", "At least one Consultation Mode is required")
    if (!form.organization_name) return ShowAlert("warning", "Organization Name is required")
    if (!form.address_line1) return ShowAlert("warning", "Address Line 1 is required")
    if (!form.city) return ShowAlert("warning", "City is required")
    if (!form.state) return ShowAlert("warning", "State is required")
    if (!form.country) return ShowAlert("warning", "Country is required")
    if (!form.pincode) return ShowAlert("warning", "Pincode is required")
    try {
      setLoading(true)
      const rawPhone = form.phone.replace(/\D/g, "")
      await createDoctor({
        name: form.name, email: form.email, countryCode: 91,
        mobileNumber: Number(rawPhone.slice(-10)),
        qualification: form.qualification,
        specialization: form.specializations.join(", "),
        yearsOfExperience: Number(form.years_of_experience),
        registrationNumber: form.registration_number,
        registrationCouncil: form.registration_council,
        registrationExpiry: form.registration_expiry ? new Date(form.registration_expiry).toISOString() : undefined,
        doctorType: form.doctor_type, organizationName: form.organization_name,
        addressLine1: form.address_line1, addressLine2: form.address_line2 || undefined,
        city: form.city, state: form.state, country: form.country, pincode: form.pincode,
        languages: form.languages, consultationModes: form.consultation_modes,
        ...(form.time_slot_start && form.time_slot_end && form.time_slot_days.length > 0 ? {
          timeSlots: { startTime: form.time_slot_start, endTime: form.time_slot_end, daysOfWeek: form.time_slot_days }
        } : {}),
      })
      ShowAlert("success", "Doctor profile created successfully")
      setForm(INITIAL)
      onCreated()
    } catch (e: any) {
      const status = e?.response?.status
      const message = e?.response?.data?.message || ""
      if (status === 409) {
        ShowAlert("warning", message)
      } else {
        ShowAlert("error", message || "Failed to create doctor profile")
      }
    } finally { setLoading(false) }
  }

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" shadow="sm">
      <Box p={6} display="flex" flexDir="column" gap={5}>

        <SectionHeader>Basic Information</SectionHeader>
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={4}>
          <Box><Label required>Full Name</Label><Input size="sm" placeholder="Jane Doe" value={form.name} onChange={(e) => set("name", e.target.value)} /></Box>
          <Box><Label required>Email</Label><Input size="sm" type="email" placeholder="doctor@hospital.com" value={form.email} onChange={(e) => set("email", e.target.value)} /></Box>
          <Box><Label required>Phone</Label><Input size="sm" type="tel" placeholder="00000 00000" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Box>
        </Box>

        <Divider />

        <SectionHeader>Professional Details</SectionHeader>
        <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={4}>
          <Box><Label required>Qualification</Label><Input size="sm" placeholder="e.g. MBBS, MD" value={form.qualification} onChange={(e) => set("qualification", e.target.value)} /></Box>
          <Box>
            <Label required>Specialization</Label>
            <MultiSelectDropdown options={SPECIALIZATIONS} value={form.specializations} onChange={(val) => set("specializations", val)} placeholder="Select specialization..." />
          </Box>
          <Box><Label required>Years of Experience</Label><Input size="sm" type="number" min={0} placeholder="e.g. 10" value={form.years_of_experience} onChange={(e) => set("years_of_experience", e.target.value)} /></Box>
          <Box>
            <Label required>Doctor Type</Label>
            <Select size="sm" placeholder="Select Type" value={form.doctor_type} onChange={(e) => set("doctor_type", e.target.value)}>
              <option value="pococare">Pococare</option>
              <option value="partner">Partner</option>
              <option value="hospital">Hospital</option>
            </Select>
          </Box>
        </Box>

        <Divider />

        <SectionHeader>Registration Details</SectionHeader>
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={4}>
          <Box><Label required>Registration Number</Label><Input size="sm" placeholder="e.g. MED-2023-8891" value={form.registration_number} onChange={(e) => set("registration_number", e.target.value)} /></Box>
          <Box>
            <Label required>Registration Council</Label>
            <Select size="sm" placeholder="Select Council" value={form.registration_council} onChange={(e) => set("registration_council", e.target.value)}>
              {COUNCILS.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Box>
          <Box><Label required>Registration Expiry</Label><Input size="sm" type="date" value={form.registration_expiry} onChange={(e) => set("registration_expiry", e.target.value)} /></Box>
        </Box>

        <Divider />

        <SectionHeader>Practice Information</SectionHeader>
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={4}>
          <Box><Label required>Organization Name</Label><Input size="sm" placeholder="e.g. Apollo Hospital" value={form.organization_name} onChange={(e) => set("organization_name", e.target.value)} /></Box>
          <Box>
            <Label required>Languages</Label>
            <MultiSelectDropdown options={LANGUAGES} value={form.languages} onChange={(val) => set("languages", val)} placeholder="Select languages..." />
          </Box>
          <Box>
            <Label required>Consultation Modes</Label>
            <Box display="flex" gap={3} mt={1}>
              {["video", "audio"].map((mode) => (
                <Box key={mode} as="label" display="flex" alignItems="center" gap={2} cursor="pointer">
                  <input type="checkbox" checked={form.consultation_modes.includes(mode)}
                    onChange={() => {
                      const val = form.consultation_modes.includes(mode)
                        ? form.consultation_modes.filter((m) => m !== mode)
                        : [...form.consultation_modes, mode]
                      set("consultation_modes", val)
                    }} />
                  <Text fontSize="sm">{mode.charAt(0).toUpperCase() + mode.slice(1)}</Text>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Divider />

        <SectionHeader>Availability <Text as="span" fontSize="xs" fontWeight="normal" color="gray.400">(optional)</Text></SectionHeader>
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={4}>
          <Box>
            <Label>Start Time</Label>
            <Select size="sm" value={form.time_slot_start} onChange={(e) => set("time_slot_start", e.target.value)} placeholder="-- : --">
              {TIME_OPTIONS.map(({ val, label }) => <option key={val} value={val}>{label}</option>)}
            </Select>
          </Box>
          <Box>
            <Label>End Time</Label>
            <Select size="sm" value={form.time_slot_end} onChange={(e) => set("time_slot_end", e.target.value)} placeholder="-- : --">
              {TIME_OPTIONS.map(({ val, label }) => <option key={val} value={val}>{label}</option>)}
            </Select>
          </Box>
          <Box>
            <Label>Days of Week</Label>
            <MultiSelectDropdown options={["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"]} value={form.time_slot_days} onChange={(val) => set("time_slot_days", val)} placeholder="Select days..." />
          </Box>
        </Box>

        <Divider />

        <SectionHeader>Address</SectionHeader>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
          <Box><Label required>Address Line 1</Label><Input size="sm" placeholder="Street / Building" value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} /></Box>
          <Box><Label>Address Line 2</Label><Input size="sm" placeholder="Area / Landmark (optional)" value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} /></Box>
        </Box>
        <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={4}>
          <Box><Label required>City</Label><Input size="sm" placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} /></Box>
          <Box><Label required>State</Label><Input size="sm" placeholder="State" value={form.state} onChange={(e) => set("state", e.target.value)} /></Box>
          <Box><Label required>Country</Label><Input size="sm" placeholder="Country" value={form.country} onChange={(e) => set("country", e.target.value)} /></Box>
          <Box><Label required>Pincode</Label><Input size="sm" placeholder="Pincode" value={form.pincode} onChange={(e) => set("pincode", e.target.value)} /></Box>
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={3} pt={2} borderTop="1px solid" borderColor="gray.200">
          <Button size="sm" variant="ghost" colorScheme="green" onClick={() => setForm(INITIAL)}>Reset</Button>
          <Button size="sm" colorScheme="green" isLoading={loading} onClick={handleSubmit}>Save Doctor Profile</Button>
        </Box>
      </Box>
    </Box>
  )
}
